import * as React from "react";
import { Alert, Button, Card, Form } from "react-bootstrap";
import styled from "styled-components";
import { MFASetupRecoveryCodes } from "./recovery-codes";

const QRBox = styled.div`
	margin-bottom: 24px;
	text-align: center;
`;

const Direction = styled.p`
	text-align: left;
`;

const QRCode = styled.img`
	height: 200px;
	width: 200px;
`;

const SecretKeyBox = styled.div`
	align-items: center;
	display: flex;
	gap: 8px;
	justify-content: center;
	word-break: break-all;
`;

export type MFASetupPageProps = {
	error?: string;
	isPending: boolean;
	manualEntryKey: string;
	onComplete: () => void;
	onSubmit: (code: string) => Promise<void>;
	qrCodeUrl: string;
	recoveryCodes?: string[];
};

/**
 * 多要素認証のセットアップ
 * - QRコードを認証アプリで読み取り、表示された認証コードで確認する
 * - 確認後はリカバリコードを表示する
 */
export const MFASetupPage = React.memo((props: MFASetupPageProps) => {
	const { error, isPending, manualEntryKey, onComplete, onSubmit, qrCodeUrl, recoveryCodes } = props;
	const [code, setCode] = React.useState<string>("");
	const [copied, setCopied] = React.useState<boolean>(false);

	const onChangeCode = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => setCode(event.target.value.trim()),
		[],
	);
	const onCopyEntryKey = React.useCallback(() => {
		navigator.clipboard
			?.writeText(manualEntryKey)
			.then(() => setCopied(true))
			.catch(() => setCopied(false));
	}, [manualEntryKey]);

	const isDisabled = isPending || code.length !== 6;

	/**
	 * Enterキー押下時のブラウザ標準のフォーム送信（ページ遷移）を抑止する
	 * - ページ遷移すると一時セッション用トークンが失われるため
	 */
	const onFormSubmit = React.useCallback(
		(event: React.FormEvent) => {
			event.preventDefault();
			if (isDisabled) return;
			onSubmit(code).catch(() => setCode(""));
		},
		[code, isDisabled, onSubmit],
	);

	return (
		<Card className="border-0 mx-auto my-5" style={{ maxWidth: "540px" }}>
			<Card.Body>
				{recoveryCodes ? (
					<MFASetupRecoveryCodes recoveryCodes={recoveryCodes} onComplete={onComplete} />
				) : (
					<>
						{error && <Alert variant={"danger"}>{error}</Alert>}
						<QRBox>
							<Direction>
								<a
									href="https://www.microsoft.com/ja-jp/account/authenticator"
									rel="noopener noreferrer"
									target="_blank"
								>
									Microsoft Authenticator
								</a>
								などの認証アプリで以下のQRコードをスキャンしてください。
								<br />
								読み取れない場合は、認証アプリにシークレットキーを入力してください。
							</Direction>
							<QRCode alt="qr-code" src={qrCodeUrl} />
							<SecretKeyBox>
								<span>シークレットキー</span>
								<code>{manualEntryKey}</code>
								<Button size="sm" variant="outline-dark" onClick={onCopyEntryKey}>
									{copied ? "コピーしました" : "コピー"}
								</Button>
							</SecretKeyBox>
						</QRBox>
						<Form noValidate onSubmit={onFormSubmit}>
							<Form.Group>
								<Form.Label>認証アプリに表示された認証コードを入力してください。</Form.Label>
								<Form.Control
									autoComplete="one-time-code"
									inputMode="numeric"
									isInvalid={!!error}
									maxLength={6}
									onChange={onChangeCode}
									placeholder="認証コード(6桁)"
									value={code}
								/>
							</Form.Group>
							<div className="my-4">
								<Button className="w-100" variant={"dark"} type="submit" disabled={isDisabled}>
									コードを認証
								</Button>
							</div>
						</Form>
					</>
				)}
			</Card.Body>
		</Card>
	);
});
